import React, { useState, useEffect } from 'react';
import { View, ScrollView, ActivityIndicator, useWindowDimensions, Pressable } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';

import { Dialog, DialogContent, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ThemedText } from '@/components/themed-text';
import { useToastStore } from '@/store/toastStore';
import { useProviderProfile, useUpdateProvider } from '@/hooks/use-provider';
import { ControlledInput } from '@/components/forms/controlled-input';
import { useForm } from 'react-hook-form';
import { cn } from '@/utils/cn';
import { getApiErrorMessage } from '@/utils/api-error';

const PRIMARY = '#FD5001';

interface PaymentSettingsFormData {
  pixCopyPaste: string;
  paymentLink: string;
  bankName: string;
  bankAgency: string;
  bankAccount: string;
  paymentInstructions: string;
}

interface EditPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function SectionLabel({
  icon,
  title,
  optional,
}: {
  icon: keyof typeof Ionicons.glyphMap;
  title: string;
  optional?: boolean;
}) {
  return (
    <View className="flex-row items-center gap-2">
      <View className="h-8 w-8 items-center justify-center rounded-lg bg-primary/10 dark:bg-primary">
        <Ionicons className="text-primary dark:text-white" name={icon} size={16} />
      </View>
      <ThemedText className="font-semibold text-base">{title}</ThemedText>
      {optional ? (
        <ThemedText className="text-xs text-foreground/40">(opcional)</ThemedText>
      ) : null}
    </View>
  );
}

export function EditPaymentModal({ open, onOpenChange, onSuccess }: EditPaymentModalProps) {
  const { addToast } = useToastStore();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isMobile = width < 768;
  const mobileModalHeight = height - insets.top - insets.bottom - 24;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: providerData } = useProviderProfile();
  const updateProvider = useUpdateProvider();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<PaymentSettingsFormData>({
    defaultValues: {
      pixCopyPaste: '',
      paymentLink: '',
      bankName: '',
      bankAgency: '',
      bankAccount: '',
      paymentInstructions: '',
    },
  });

  useEffect(() => {
    if (open && providerData) {
      reset({
        pixCopyPaste: providerData.defaultPixCopyPaste || '',
        paymentLink: providerData.defaultPaymentLink || '',
        bankName: providerData.defaultBankDetails?.bankName || '',
        bankAgency: providerData.defaultBankDetails?.bankAgency || '',
        bankAccount: providerData.defaultBankDetails?.bankAccount || '',
        paymentInstructions: providerData.defaultBankDetails?.paymentInstructions || '',
      });
    }
  }, [open, providerData, reset]);

  const onSubmit = async (data: PaymentSettingsFormData) => {
    setIsSubmitting(true);

    try {
      await updateProvider.mutateAsync({
        defaultPixCopyPaste: data.pixCopyPaste || undefined,
        defaultPaymentLink: data.paymentLink || undefined,
        defaultBankDetails: {
          bankName: data.bankName || undefined,
          bankAgency: data.bankAgency || undefined,
          bankAccount: data.bankAccount || undefined,
          paymentInstructions: data.paymentInstructions || undefined,
        },
      });

      addToast('Dados de pagamento atualizados com sucesso!', 'success');
      onSuccess?.();
    } catch (error: any) {
      console.error('Erro ao atualizar dados de pagamento:', error);
      addToast(getApiErrorMessage(error, 'Erro ao atualizar dados de pagamento.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  const contentClassName = isMobile
    ? 'w-full overflow-hidden rounded-3xl p-0 shadow-2xl'
    : 'w-[95%] min-w-[600px] max-h-[90vh] flex flex-col overflow-hidden rounded-2xl p-0';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={contentClassName}
        style={
          isMobile
            ? {
                height: mobileModalHeight,
                maxHeight: mobileModalHeight,
                width: width - 24,
                flexDirection: 'column',
              }
            : { flexDirection: 'column' }
        }
      >
        <View
          style={isMobile ? { flex: 1, flexDirection: 'column' } : undefined}
          className={isMobile ? undefined : 'flex flex-col'}
        >
          <View
            className={cn(
              'bg-neutral-50/90 px-5 pb-4',
              isMobile ? 'pt-5' : 'px-6 pt-6',
            )}
          >
            <View className="flex-row items-start justify-between gap-3">
              <View className="flex-1 gap-2">
                <View className="flex-row items-center gap-3">
                  <View className="h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 dark:bg-primary">
                    <Ionicons className="text-primary dark:text-white" name="wallet-outline" size={22} />
                  </View>
                  <View className="flex-1">
                    <ThemedText
                      className={cn('font-bold leading-snug', isMobile ? 'text-lg' : 'text-xl')}
                    >
                      Dados de pagamento
                    </ThemedText>
                    <ThemedText className="mt-0.5 text-sm leading-5 text-foreground/55">
                      Configure suas informações de recebimento
                    </ThemedText>
                  </View>
                </View>
              </View>

              <Pressable
                onPress={handleClose}
                className="h-9 w-9 items-center justify-center rounded-full bg-neutral-200/80"
                hitSlop={8}
              >
                <Ionicons className="text-neutral-400" name="close" size={18} />
              </Pressable>
            </View>
          </View>

          <ScrollView
            style={isMobile ? { flex: 1, minHeight: 0 } : undefined}
            className={isMobile ? undefined : 'max-h-[50vh]'}
            contentContainerClassName="px-5 py-5"
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            <View>
              <View className="mb-6">
                <View className="mb-2.5">
                  <SectionLabel icon="qr-code-outline" title="Chave Pix" />
                </View>
                <ControlledInput
                  control={control}
                  name="pixCopyPaste"
                  placeholder="Cole aqui seu código Pix ou chave"
                />
                <ThemedText className="mt-2 text-xs leading-4 text-foreground/45">
                  O cliente copia e paga direto pelo Pix.
                </ThemedText>
              </View>

              <View className="mb-6">
                <View className="mb-2.5">
                  <SectionLabel icon="link-outline" title="Link de pagamento" />
                </View>
                <ControlledInput
                  control={control}
                  name="paymentLink"
                  placeholder="https://..."
                  keyboardType="url"
                />
                <ThemedText className="mt-2 text-xs leading-4 text-foreground/45">
                  PagSeguro, Mercado Pago ou outro link externo.
                </ThemedText>
              </View>

              <View className="mb-6 rounded-2xl border border-border/50 bg-neutral-50/80 p-4">
                <View className="mb-3.5">
                  <SectionLabel icon="card-outline" title="Dados bancários" optional />
                </View>

                <View className="mb-3">
                  <ControlledInput
                    control={control}
                    name="bankName"
                    label="Nome do banco"
                    placeholder="Ex: Nubank, Itaú, Bradesco"
                  />
                </View>

                <View className="mb-3 flex-row">
                  <View className="mr-3 flex-1">
                    <ControlledInput
                      control={control}
                      name="bankAgency"
                      label="Agência"
                      placeholder="0000"
                      keyboardType="numeric"
                    />
                  </View>
                  <View className="flex-[2]">
                    <ControlledInput
                      control={control}
                      name="bankAccount"
                      label="Conta"
                      placeholder="00000-0"
                    />
                  </View>
                </View>

                <ControlledInput
                  control={control}
                  name="paymentInstructions"
                  label="Instruções"
                  placeholder="Orientações extras para o cliente..."
                  type="textarea"
                />
              </View>

              <View className="flex-row items-start rounded-2xl bg-primary/5 px-4 py-3.5">
                <Ionicons name="information-circle-outline" size={20} color={PRIMARY} />
                <ThemedText className="ml-3 flex-1 text-xs leading-5 text-foreground/65">
                  Essas informações viram padrão nas cobranças manuais. Você pode ajustar em cada
                  cobrança.
                </ThemedText>
              </View>
            </View>
          </ScrollView>

          <View className={cn('px-5 pb-4 pt-2', !isMobile && 'px-6')}>
            <DialogFooter className="flex-row justify-end gap-3">
              <Button
                variant="outline"
                onPress={handleClose}
                disabled={isSubmitting}
              >
                <ThemedText>Cancelar</ThemedText>
              </Button>
              <Button
                onPress={handleSubmit(onSubmit)}
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? (
                  <View className="flex-row items-center gap-2">
                    <ActivityIndicator size="small" color="white" />
                    <ThemedText className="text-white">Salvando...</ThemedText>
                  </View>
                ) : (
                  <ThemedText className="text-white">Salvar</ThemedText>
                )}
              </Button>
            </DialogFooter>
          </View>
        </View>
      </DialogContent>
    </Dialog>
  );
}
